import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppArchiveStore - the one thing this page remembers between visits.
//
// ==================================================================================================
// !! ⛔ THIS IS A DELIBERATE EXCEPTION TO "NOTHING GOES IN `localStorage`", AND THE LINE IS THE POWER
// ==================================================================================================
//
// `AppMain.Secret` is explicitly kept OUT of storage, and its comment says why: it "authorises
// requesting a pairing code against this number and - once the handset has proved it - pointing this
// subscriber's entire archive at a Dropbox". ⭐ THE ARCHIVE KEY DOES NEITHER. All it can buy is a
// `files.content.read` ticket for that one subscriber's own folder, so the worst a stolen one yields
// is READING an archive - never sending a message, never requesting a pairing code that wedges a
// handset, never moving anybody's storage. **The distinction is what the credential can DO, not where
// it is kept**, and without something durable here the archive is openable only in the two hours
// after sign-up, which is not the product that was promised.
//
// ⚠ SAID PLAINLY: whoever gets script into this origin, or reads this device's profile, can read this
// subscriber's message history until the key expires or is revoked. That is the cost. It is bounded
// by being read-only, by thirty sliding days, and by `ArchiveKey`'s one-blob revocation.
//
// ==================================================================================================
// !! ⛔ THE NUMBER IS STORED TOO, AND IT HAS TO BE
// ==================================================================================================
//
// A cold page has no number either - `SignUp.Number` means "enrolled in THIS session". `/folder` is
// addressed by number, so a key with no number to present it against opens nothing. ⚠ It is the
// subscriber's own number on the subscriber's own device, which is where it already is.
//
// ==================================================================================================
// !! ⛔ EVERY CALL IS WRAPPED, BECAUSE `localStorage` THROWS RATHER THAN RETURNING NULL
// ==================================================================================================
//
// Private browsing, a blocked-storage policy and a full quota all make the ACCESSOR throw, not answer
// empty. ⚠ An unreachable store is an EXPECTED condition (law 1, fate 1): the page falls back to
// having no key and says the ordinary sentence. Reporting it would flip `KErrorMode` and blank the
// archive over a browser setting.

export class AppArchiveStore {
    /** ⚠ PREFIXED, because this origin is shared with whatever else is ever served from it. */
    public static readonly NumberAt: string = "wac.archive.number";
    public static readonly KeyAt: string = "wac.archive.key";

    /**
     * Keep a number and key for next time.
     *
     * ⛔ AN EMPTY KEY IS A NO-OP, NEVER A WRITE. The service answers `key: ""` on every ordinary
     * visit to mean "keep the one you have", so storing it would erase a working credential on the
     * very visits that prove it works - and only for subscribers who came back, which is the hardest
     * kind of bug to see.
     */
    public static Save = (Number: string, Key: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const One: string = nz(Number).trim();
            const Two: string = nz(Key).trim();
            if (One.length === 0 || Two.length === 0) { return; }
            window.localStorage.setItem(AppArchiveStore.NumberAt, One);
            window.localStorage.setItem(AppArchiveStore.KeyAt, Two);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an unwritable store is EXPECTED (see the header) - the
            // subscriber simply has to open their archive from the sign-up again next time.
            return;
        }
    };

    /**
     * DOCUMENTED EXCEPTION: the catch returns "" rather than reporting.
     * ERROR-SUPPRESSED-JUSTIFIED: an unreadable store is EXPECTED - see the header.
     */
    public static Number = (): string => {
        try {
            return nz(window.localStorage.getItem(AppArchiveStore.NumberAt)).trim();
        } catch (e) {
            return "";
        }
    };

    /**
     * DOCUMENTED EXCEPTION: the catch returns "" rather than reporting.
     * ERROR-SUPPRESSED-JUSTIFIED: an unreadable store is EXPECTED - see the header.
     */
    public static Key = (): string => {
        try {
            return nz(window.localStorage.getItem(AppArchiveStore.KeyAt)).trim();
        } catch (e) {
            return "";
        }
    };

    /**
     * Is there a usable pair here?
     *
     * ⛔ BOTH OR NEITHER. A key with no number cannot address `/folder`, and a number with no key
     * proves nothing - so a half-written pair must read as nothing rather than as something that
     * will fail one round trip later.
     */
    public static Held = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppArchiveStore.Number().length > 0 && AppArchiveStore.Key().length > 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppArchiveStore.Held");
            return false;
        }
    };

    /**
     * Throw the key away.
     *
     * ⭐ CALLED WHEN THE SERVICE REFUSES IT, so a key that has expired or been revoked stops being
     * retried on every visit for ever. ⚠ THE NUMBER GOES TOO: a number with no key is exactly the
     * half-written pair `Held` refuses, and leaving it would be litter that looks like state.
     */
    public static Forget = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            window.localStorage.removeItem(AppArchiveStore.KeyAt);
            window.localStorage.removeItem(AppArchiveStore.NumberAt);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an unwritable store is EXPECTED (see the header). The key
            // it holds is already known to be refused, so failing to remove it costs one wasted
            // request per visit and nothing else.
            return;
        }
    };
}
